import { Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { animate, style, transition, trigger } from '@angular/animations';

import { filter, map, switchMap, tap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

import { ShoppingCartService } from '@shared/services/cart/shopping-cart.service';
import { BlockAction, ViewPart, ViewState } from '@core/models';
import { SubSink } from '@core/utils/subsink';
import { Block } from '@core/models/block/block.model';
import { BlockActionType, clientPageTypeEnum } from '@core/enums';
import { ApiClientService, Slug } from '@core/services/client/api-client.service';
import { ButtonHandlerService } from '@core/services/button-handler.service';
import { AuthService } from '@app/auth/services/auth.service';
import { SessionStorageService } from '@app/shared/services/storage';
import { environment } from '@env/environment';

// ** The dropdown styles animations */
export const dropDownAnimations = trigger('dropDownAnimations', [
  transition(':enter', [
    style({opacity: 0}),
    animate('100ms', style({opacity: 1})),
  ]),
  transition(':leave', [
    animate('100ms', style({opacity: 0}))
  ])
]);

/**
 * Filter close action
 */
const filterRedirect = (action: BlockAction) => action.actionType === BlockActionType.REDIRECT;
const filterOpen = (action: BlockAction) => action.actionType === BlockActionType.OPEN;
const filterSubmit = (action: BlockAction) => action.actionType === BlockActionType.SUBMIT;

@Component({
    selector: 'my-account-dropdown',
    template: `
    <div #dropdown class="my-account-dropdown">
      <div app-block
           *ngIf="dropDownToggle"
           [class]="dropDownToggle.uId"
           [item]="dropDownToggle"
           [appClickableBlock]="dropDownToggle">
      </div>

      <div app-block
           class="drop-down"
           *ngIf="_isOpen"
           [@dropDownAnimations]
           [class]="dropDownDown.uId"
           [item]="dropDownDown">
      </div>
    </div>
  `,
    styleUrls: ['./my-account-dropdown.component.scss'],
    encapsulation: ViewEncapsulation.None,
    animations: [dropDownAnimations],
    providers: [ButtonHandlerService],
    standalone: false
})

export class MyAccountDropdownComponent implements OnInit, OnDestroy {
  @Input() specialBlock: Block;
  _isOpen = false;
  dropDownToggle: Block | undefined;
  dropDownDown: Block | undefined;
  buttonClick$: Observable<BlockAction> = this.buttonService.click$;
  @ViewChild('dropdown', {read: ElementRef}) dropdown: ElementRef;
  locale = this.storageService.getItem('language');
  /** Subskrypcje wywoływane raz (ngOnInit): user$, redirect, toggle, submit. Nie czyścimy przy initialize(). */
  private subSink = new SubSink();
  /** Tylko subskrypcje koszyka (syncCart$, add$, logout$). Przy każdym initialize(LOGIN) najpierw unsubscribe(), potem nowe – żeby nie dublować POST. */
  private cartSubs = new SubSink();

  constructor(private router: Router,
              private clientService: ApiClientService,
              private authService: AuthService,
              private shoppingCartService: ShoppingCartService,
              private storageService: SessionStorageService,
              private buttonService: ButtonHandlerService) {
  }

  // clicked outside the box
  @HostListener('document:click', ['$event'])
  documentClick(event) {
    if (this.dropdown && !this.dropdown.nativeElement.contains(event.target) && this._isOpen) {
      this._isOpen = false;
    }
  }

  ngOnInit() {
    this.authService.autoLogin();
    this.initialize(this.authService.state.getValue() ? ViewState.LOGIN : ViewState.UNKNOWN);

    // subscribe for  user login changed
    this.subSink.sink =
      this.authService.user$.pipe(
        map((user: any | null) => user ? ViewState.LOGIN : ViewState.UNKNOWN),
        tap((state: string) => this.initialize(state))
      ).subscribe();

    // redirect subscription
    this.subSink.sink =
      this.buttonClick$
        .pipe(
          filter(filterRedirect),
          map(({actionData}) => (actionData.pageType ?? actionData.url) || -1),
          switchMap((pageTypeOrUrl: clientPageTypeEnum | string) => {
            return ((pageTypeOrUrl in clientPageTypeEnum)
              ? this.clientService.getPageSlug(pageTypeOrUrl as clientPageTypeEnum)
              : of({slug: pageTypeOrUrl})) as Observable<Slug>;
          }),
          map(({slug}) => {
            if (slug.startsWith('/')) {
              slug = slug.replace(/^\//, '');
            }
            const languages = environment.availableLocales;
            const startsWithLang = languages.some(lang => slug.startsWith(lang + '/'));

            if (!startsWithLang) {
              const locale = this.storageService.getItem('language');
              slug = `${locale}/${slug}`;
            }
            return slug = `/${slug}`;
          }))
        .subscribe((slug) => this.router.navigateByUrl(slug));

    // if login state then subscribe to other action
    // toggle open close button for login state
    this.subSink.sink =
      this.buttonClick$
        .pipe(filter(filterOpen))
        .subscribe(_ => this.toggleDropDown());

    // Wylogowanie — jedna subskrypcja na cały cykl życia komponentu. Wcześniej stała
    // w initialize(), a ta metoda składa widok więcej niż raz przy jednym wejściu na
    // stronę (raz wprost powyżej, raz z emisji user$). Subskrypcje odkładały się w
    // subSink, którego — inaczej niż cartSubs — initialize() nie czyści, więc jedno
    // kliknięcie uruchamiało wylogowanie tyle razy, ile razy złożono widok. Poświadczenie
    // czytane jest z magazynu, a każde wylogowanie ten magazyn czyści: kolejne wywołania
    // szły z pustym nagłówkiem, serwer odrzucał je kodem 401 i sesja zostawała otwarta.
    this.subSink.sink =
      this.buttonClick$
        .pipe(
          filter(filterSubmit),
          switchMap(_ => this.authService.logout()))
        .subscribe(_ => this.router.navigate([`./${this.storageService.getItem('language')}`]));
  }


  initialize(viewState: string) {
    const currentViewState = this.specialBlock.children.find(b => b.settings.viewState === viewState);

    this.dropDownToggle = currentViewState.children.find(b => b.settings.viewPart === ViewPart.DROP_DOWN_TOGGLE);

    this._isOpen = false;

    // if unknown ignore the rest
    if (viewState === ViewState.UNKNOWN) {
      return;
    }

    this.dropDownDown = currentViewState.children.find(b => b.settings.viewPart === ViewPart.DROP_DOWN_DOWN);

    this.cartSubs.unsubscribe();

    // Subskrypcja = „uruchom strumień”. syncCart$/add$/logout$ robią side-effecty (POST, aktualizacja koszyka) wewnątrz pipe – nie zwracamy wyniku do template.
    this.cartSubs.sink = this.shoppingCartService.syncCart$.subscribe();
    this.cartSubs.sink = this.shoppingCartService.add$.subscribe();
    this.cartSubs.sink = this.shoppingCartService.logout$.subscribe();
  }

  toggleDropDown() {
    this._isOpen = !this._isOpen;
  }

  ngOnDestroy(): void {
    this.cartSubs.unsubscribe();
    this.subSink.unsubscribe();
  }
}
